import React, { useEffect, useState, useCallback } from 'react';
import {
  fetchRewardItems,
  saveRewardItem,
  setRewardItemActive,
  fetchRedemptions,
  resolveRedemption,
  formatIsoDate,
} from '../../lib/explorerRewardsApi';
import { usePortalT } from '../../i18n/usePortalT';
import {
  PageTitle,
  PageSubtitle,
  Btn,
  BtnDanger,
  BtnRow,
  Card,
  Table,
  Badge,
  ErrorText,
  Field,
} from '../../styles/adminStyles';

const EMPTY_FORM = {
  id: null,
  title: '',
  description: '',
  imageUrl: '',
  costPoints: 100,
  stock: '',
  sortOrder: 0,
};

const REDEMPTION_STATUS_VALUES = ['pending', 'all', 'fulfilled', 'cancelled'];

export default function RewardStorePage() {
  const { t, tEnum, language } = usePortalT();
  const [items, setItems] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [redemptionStatus, setRedemptionStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [itemRows, redemptionRows] = await Promise.all([
        fetchRewardItems(),
        fetchRedemptions({ status: redemptionStatus }),
      ]);
      setItems(itemRows);
      setRedemptions(redemptionRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [redemptionStatus]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setForm({
      id: item.id,
      title: item.title || '',
      description: item.description || '',
      imageUrl: item.imageUrl || '',
      costPoints: item.costPoints ?? 100,
      stock: item.stock == null ? '' : String(item.stock),
      sortOrder: item.sortOrder ?? 0,
    });
    setShowForm(true);
  };

  const onSubmitForm = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.title.trim()) { setError(t('rewardStore.needTitle')); return; }
    const cost = Number(form.costPoints);
    if (!Number.isFinite(cost) || cost <= 0) { setError(t('rewardStore.needCost')); return; }
    setBusy(true);
    setError(null);
    setMsg(null);
    const res = await saveRewardItem({
      id: form.id,
      title: form.title.trim(),
      description: form.description.trim() || null,
      imageUrl: form.imageUrl.trim() || null,
      costPoints: Math.round(cost),
      stock: form.stock === '' ? null : Math.max(0, Math.round(Number(form.stock) || 0)),
      sortOrder: Math.round(Number(form.sortOrder) || 0),
    });
    setBusy(false);
    if (res.success) {
      setMsg(form.id ? t('rewardStore.itemUpdated') : t('rewardStore.itemCreated'));
      setShowForm(false);
      load();
    } else {
      setError(res.error || t('common.saveFailed'));
    }
  };

  const onToggleActive = async (item) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setMsg(null);
    const res = await setRewardItemActive(item.id, !item.active);
    setBusy(false);
    if (res.success) {
      setMsg(item.active ? t('rewardStore.putOffDone') : t('rewardStore.putOnDone'));
      load();
    } else {
      setError(res.error || t('common.updateFailed'));
    }
  };

  const onResolveRedemption = async (redemptionId, action) => {
    if (busy) return;
    const note = window.prompt(action === 'fulfill' ? t('rewardStore.promptFulfill') : t('rewardStore.promptCancel'), '') ?? null;
    if (note === null && action === 'cancel') return;
    setBusy(true);
    setError(null);
    setMsg(null);
    const res = await resolveRedemption(redemptionId, { action, note: note || null });
    setBusy(false);
    if (res.success) {
      setMsg(action === 'fulfill' ? t('rewardStore.fulfilled') : t('rewardStore.cancelledRefunded'));
      load();
    } else {
      setError(res.error || t('reports.resolveFailed'));
    }
  };

  return (
    <div>
      <PageTitle>{t('rewardStore.title')}</PageTitle>
      <PageSubtitle>
        {t('rewardStore.subtitle')}
      </PageSubtitle>

      {msg ? <p style={{ color: '#15803d', fontWeight: 700 }}>{msg}</p> : null}
      {error ? <ErrorText>{error}</ErrorText> : null}
      {loading ? <p>{t('common.loading')}</p> : null}

      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0, fontSize: 17 }}>{t('rewardStore.itemsHeading', { n: items.length })}</h2>
          <Btn $primary type="button" onClick={openCreate}>{t('rewardStore.newItem')}</Btn>
        </div>

        {showForm ? (
          <form onSubmit={onSubmitForm} style={{ marginTop: 16, borderTop: '1px solid #e4e4e7', paddingTop: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
              <Field style={{ marginBottom: 0 }}>
                {t('rewardStore.fName')}
                <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder={t('rewardStore.fNamePh')} />
              </Field>
              <Field style={{ marginBottom: 0 }}>
                {t('rewardStore.fCost')}
                <input type="number" min="1" value={form.costPoints} onChange={(e) => setForm((f) => ({ ...f, costPoints: e.target.value }))} />
              </Field>
              <Field style={{ marginBottom: 0 }}>
                {t('rewardStore.fStock')}
                <input type="number" min="0" value={form.stock} onChange={(e) => setForm((f) => ({ ...f, stock: e.target.value }))} />
              </Field>
              <Field style={{ marginBottom: 0 }}>
                {t('rewardStore.fSort')}
                <input type="number" value={form.sortOrder} onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))} />
              </Field>
            </div>
            <Field style={{ marginTop: 14 }}>
              {t('rewardStore.fDesc')}
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder={t('rewardStore.fDescPh')} />
            </Field>
            <Field>
              {t('rewardStore.fImage')}
              <input value={form.imageUrl} onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))} placeholder="https://…" />
            </Field>
            <BtnRow style={{ marginTop: 6 }}>
              <Btn $primary type="submit" disabled={busy}>{form.id ? t('rewardStore.saveEdit') : t('common.create')}</Btn>
              <Btn type="button" onClick={() => setShowForm(false)}>{t('common.cancel')}</Btn>
            </BtnRow>
          </form>
        ) : null}

        {!loading && items.length === 0 ? <p style={{ color: '#71717a' }}>{t('rewardStore.emptyItems')}</p> : null}

        {items.length > 0 ? (
          <Table style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>{t('rewardStore.colName')}</th>
                <th>{t('rewardStore.colCost')}</th>
                <th>{t('rewardStore.colStock')}</th>
                <th>{t('rewardStore.colRedeemed')}</th>
                <th>{t('common.status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td style={{ fontWeight: 700 }}>
                    <div>{item.title}</div>
                    {item.description ? (
                      <div style={{ fontSize: 12, color: '#71717a', fontWeight: 500, maxWidth: 320 }}>{item.description}</div>
                    ) : null}
                  </td>
                  <td>{item.costPoints}</td>
                  <td>{item.stock == null ? t('common.unlimited') : item.stock}</td>
                  <td>{item.redeemedCount ?? 0}</td>
                  <td><Badge $status={item.active ? 'active' : 'ended'}>{item.active ? t('rewardStore.onShelf') : t('rewardStore.offShelf')}</Badge></td>
                  <td>
                    <BtnRow style={{ marginTop: 0 }}>
                      <Btn type="button" disabled={busy} onClick={() => openEdit(item)}>{t('common.edit')}</Btn>
                      {item.active ? (
                        <BtnDanger type="button" disabled={busy} onClick={() => onToggleActive(item)}>{t('rewardStore.putOff')}</BtnDanger>
                      ) : (
                        <Btn type="button" disabled={busy} onClick={() => onToggleActive(item)}>{t('rewardStore.putOn')}</Btn>
                      )}
                    </BtnRow>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px 0' }}>
          <h2 style={{ margin: 0, fontSize: 17, flex: 1 }}>{t('rewardStore.requestsHeading')}</h2>
          <Field style={{ marginBottom: 0, minWidth: 140 }}>
            {t('common.status')}
            <select value={redemptionStatus} onChange={(e) => setRedemptionStatus(e.target.value)}>
              {REDEMPTION_STATUS_VALUES.map((v) => (
                <option key={v} value={v}>{tEnum('redemptionStatusFilter', v)}</option>
              ))}
            </select>
          </Field>
          <Btn type="button" onClick={load}>{t('common.refresh')}</Btn>
        </div>

        {!loading && redemptions.length === 0 ? (
          <p style={{ padding: '16px 20px', color: '#71717a' }}>{t('rewardStore.emptyRequests')}</p>
        ) : null}

        {redemptions.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <th>{t('rewardStore.colUser')}</th>
                <th>{t('rewardStore.colItem')}</th>
                <th>{t('rewardStore.colSpent')}</th>
                <th>{t('common.status')}</th>
                <th>{t('rewardStore.colRequestedAt')}</th>
                <th>{t('rewardStore.colNote')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {redemptions.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 700 }}>{r.userName || r.userId}</td>
                  <td>{r.itemTitle || r.itemId}</td>
                  <td>{r.pointsSpent}</td>
                  <td>
                    <Badge $status={r.status === 'pending' ? 'active' : 'ended'}>
                      {tEnum('redemptionStatus', r.status)}
                    </Badge>
                  </td>
                  <td style={{ fontSize: 12 }}>{formatIsoDate(r.createdAt, language)}</td>
                  <td style={{ maxWidth: 220, fontSize: 12 }}>{r.note || '—'}</td>
                  <td>
                    {r.status === 'pending' ? (
                      <BtnRow style={{ marginTop: 0 }}>
                        <Btn $primary type="button" disabled={busy} onClick={() => onResolveRedemption(r.id, 'fulfill')}>{t('rewardStore.fulfill')}</Btn>
                        <BtnDanger type="button" disabled={busy} onClick={() => onResolveRedemption(r.id, 'cancel')}>{t('rewardStore.cancelRefund')}</BtnDanger>
                      </BtnRow>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>
    </div>
  );
}
